import { useMemo } from 'react'
import { Column, Cell } from 'react-table'
import { Button, ButtonProps } from '@ritualco/jutsu'
import { BasicTable } from 'components/BasicTable'
import { useContent } from 'lib/content/react'
import { Merchant, WorkspaceMerchant } from 'types/entities'

const AddButton = (props: ButtonProps) => (
  <Button size="sm" isFullWidth {...props} />
)

const RemoveButton = (props: ButtonProps) => (
  <Button
    size="sm"
    colorScheme="transparent"
    variant="outline"
    isFullWidth
    {...props}
  />
)

export type MerchantManageTableProps = {
  /** List of merchants that can be added/removed from the merchant list */
  workspaceMerchants?: WorkspaceMerchant[]
  /** List of merchants currently in the merchant list. Used to determine
  which of the network merchants are addable and which are removable. */
  merchants: Merchant[]
  onAdd: (merchantId: string) => void
  onRemove: (merchantId: string) => void
}

export const MerchantManageTable = (props: MerchantManageTableProps) => {
  const { workspaceMerchants = [], merchants, onAdd, onRemove } = props
  const { formatMessage } = useContent()
  const columns = useMemo<Column<WorkspaceMerchant>[]>(
    () => [
      {
        accessor: 'name',
        Header: formatMessage({ id: 'pmportal.restaurantLists.restaurant' }),
      },
      {
        accessor: 'address',
        Header: formatMessage({ id: 'pmportal.restaurantLists.address' }),
      },
      {
        accessor: 'inMerchantNetwork',
        Header: formatMessage({ id: 'pmportal.restaurantLists.inNetwork' }),
        Cell: ({ value }) =>
          formatMessage({ id: `pmportal.global.${value ? 'yes' : 'no'}` }),
      },
      {
        id: 'inList',
        accessor: row => merchants.some(({ id }) => id === row.id),
        Cell: ({ row, value }: Cell<Merchant>) =>
          value ? (
            <RemoveButton
              onClick={() => onRemove(row.original.id)}
              width="124px"
            >
              {formatMessage({ id: 'pmportal.global.actionRemove' })}
            </RemoveButton>
          ) : (
            <AddButton onClick={() => onAdd(row.original.id)} width="124px">
              {formatMessage({ id: 'pmportal.global.actionAdd' })}
            </AddButton>
          ),
      },
    ],
    [formatMessage, merchants, onAdd, onRemove]
  )

  return (
    <BasicTable
      columns={columns}
      data={workspaceMerchants}
      placeholder={formatMessage({
        id: 'pmportal.restaurantLists.noRestaurantsAvailable',
      })}
    />
  )
}
